import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';
import { bindActionCreators } from 'redux';

import fetchSalon from 'actions/fetchSalon.action';
import ICO_EMAIL from 'assets/images/online-booking/email-icon.svg';
import Button from 'components/form/Button.component';
import Toggler from 'components/form/Toggler.component';
import Foldout from 'components/generic/Foldout.component';
import OverlaySpinner from 'components/generic/OverlaySpinner.component';
import withSalonId from 'components/hoc/withSalonId.hoc';
import LINK from 'constants/link.const';
import useAef from 'hooks/useAef.hook';
import useAfn from 'hooks/useAfn.hook';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme, withTheme } from 'providers/Theme.provider';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import linkTo from 'utils/links/linkTo.util';
import logger from 'utils/logger.util';
import asObject from 'utils/objects/asObject.util';

import beGetSettingsNcob from 'settings/actions/beGetSettingsNcob';
import bePutSettingsNcob from 'settings/actions/bePutSettingsNcob';
import SectionSeparator from 'settings/online-booking/pages/SectionSeparator.component';
import EmailNotificationBookingConfirmation from 'settings/online-booking/pages/text-alerts/EmailNotificationBookingConfirmation.component';

import CN from './TextAlertsPage.module.scss';

const L = logger('TextAlertsPage');
const REGEX = /^\S+@\S+\.\S+$/;

const TextAlertsPage = ({ fetchSalon, salonId, beGetSettingsNcob, bePutSettingsNcob }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();

  const [ncobEmail, setNcobEmail] = useStateWhileMounted('');
  const [hasConfirmEmail, setConfirmEmail] = useState(false);
  const { val: isEmailValid, set: setIsEmailValid } = useTip(false);
  const { nay: hideError } = useTip(false);

  const [{ isLoading: isSaveLoading }, handleSaveEmail] = useAfn(
    async ({ ncobEmail }) => {
      // The validation remains even when the field is empty,
      // we can save blank email, as this is how actually switch it off

      if (ncobEmail && !fromAnyToString(ncobEmail).match(REGEX)) {
        setIsEmailValid(false);
        return;
      }

      try {
        const trimmedEmail = fromAnyToTrimmed(ncobEmail);
        const result = await bePutSettingsNcob({ data: { salon_ncob_email: trimmedEmail } });
        const json = asObject(result.json);

        // eslint-disable-next-line consistent-return
        if (!result.is.ok) return void L.warn('handleSaveEmail()', 'result:', result);

        setNcobEmail(json.salon_ncob_email);
      } catch (e) {
        L.warn('()', 'handleSaveEmail error:', e);
      }
    },
    [bePutSettingsNcob, setIsEmailValid, setNcobEmail],
  );

  const handleConfirmEmail = async () => {
    try {
      const resultSendEmail = await bePutSettingsNcob({ data: { send_ncob_email_to_stylists: !hasConfirmEmail } });

      if (!resultSendEmail.is.ok) {
        L.warn('handleConfirmEmail()', 'resultSendEmail:', resultSendEmail);
        return;
      }

      setConfirmEmail(resultSendEmail.json.send_ncob_email_to_stylists);
    } catch (e) {
      L.warn('()', 'resultSendEmail error:', e);
    }
  };

  useAef(async () => {
    try {
      const result = await beGetSettingsNcob();
      const json = asObject(result.json);

      if (!result.is.ok) {
        L.warn('beGetSettingsNcob()', 'result:', result);
        return false;
      }

      setNcobEmail(json.salon_ncob_email);
      setConfirmEmail(json.send_ncob_email_to_stylists);

      return true;
    } catch (e) {
      L.warn('()', 'beGetSettingsNcob error:', e);
      return false;
    }
  }, [beGetSettingsNcob, setConfirmEmail, setNcobEmail]);

  useEffect(
    //
    () => void L.unignore('useEffect()', 'fetchSalon:', fetchSalon(salonId)),
    [fetchSalon, salonId],
  );

  useEffect(() => {
    setIsEmailValid(fromAnyToString(ncobEmail).match(REGEX)); // Disable button on first render if no email
  }, [ncobEmail, setIsEmailValid]);

  return (
    <div
      data-bem="TextAlertsPage"
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
      })}
    >
      {isSaveLoading && <OverlaySpinner />}
      <Foldout tag="TextAlertsPage__email--alerts" src={ICO_EMAIL} title={t('onlineBooking.emailNotifications.staffAlerts.foldoutTitle')}>
        <SectionSeparator />
        <div className={CN.containerAlert}>
          <div className={CN.content}>
            <div className={CN.title}>{t('onlineBooking.emailNotifications.staffAlerts.titleSection1')}</div>
            <div className={CN.text}>{t('onlineBooking.emailNotifications.staffAlerts.description1')}</div>
            <div className={CN.text}>
              {t('onlineBooking.emailNotifications.staffAlerts.description2')}
              <div className={CN.inputContainer}>
                <input
                  className={CN.input}
                  type="text"
                  placeholder={t('onlineBooking.emailNotifications.staffAlerts.placeholder')}
                  value={ncobEmail}
                  onChange={(e) => {
                    setNcobEmail(e.target.value);
                    setIsEmailValid(REGEX.test(e.target.value));
                    hideError();
                  }}
                />
              </div>
            </div>
            <div className={CN.buttonsContainer}>
              {!isEmailValid && ncobEmail && ncobEmail.trim() !== '' && <div className={CN.error}>{t('onlineBooking.emailNotifications.staffAlerts.emailWarning')}</div>}
              <div className={CN.relative}>
                <Button onClick={() => handleSaveEmail({ ncobEmail })} variant="primary" disabled={!isEmailValid && fromAnyToTrimmed(ncobEmail)}>
                  {t('common.buttons.saveUpp')}
                </Button>
              </div>
            </div>
          </div>
          <div className={CN.imageEmail} />
        </div>
        <div className={CN.containerNotify}>
          <div className={CN.content}>
            <div className={CN.notifyTitle}>
              <div>{t('onlineBooking.emailNotifications.staffAlerts.titleSection2')}</div>
              <div className={CN.togglerContainer}>
                <Toggler onClick={handleConfirmEmail} selected={hasConfirmEmail} />
              </div>
            </div>
            <div className={CN.text}>{t('onlineBooking.emailNotifications.staffAlerts.description3')}</div>
            <div className={CN.text}>
              {t('onlineBooking.emailNotifications.staffAlerts.description4')}
              <div className={CN.linkContainer}>
                <NavLink to={linkTo({ pattern: LINK.settingStylist, params: { salonId }, fault: '#' })}>
                  <div className={CN.link}>{t('onlineBooking.emailNotifications.staffAlerts.linkText')}</div>
                </NavLink>
              </div>
            </div>
          </div>
          <div className={CN.imageNotification} />
        </div>
      </Foldout>
      {/* EmailNotificationBookingConfirmation */}
      <EmailNotificationBookingConfirmation />
    </div>
  );
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchSalon,
      beGetSettingsNcob,
      bePutSettingsNcob,
    },
    dispatch,
  );

const TextAlertsPageHoc = connect(null, mapDispatchToProps)(withTheme()(withSalonId(TextAlertsPage)));

export default TextAlertsPageHoc;
